import React, { Component } from 'react';
import { every, some } from 'lodash';
import { routes } from 'constants/config';
import { Card } from 'ui/';
import request from 'utils/request_ajax';
import { getQueryParam } from 'utils/url';
import SessionDetails from 'components/InPersonTraining/shared/SessionDetails';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import FeatureFlag from 'components/shared/FeatureFlag';
import Header from './Header';
import AttendanceForm from './AttendanceForm';
import AttendanceStatus from './AttendanceStatus';

class EventRegistrationOverview extends Component {
  state = {
    changingAttendance: false,
    attendance: this.props.attendance,
    statusLoading: null,
    rsvpStatus: null,
  };

  setUserAttendance = attendanceStatus => {
    const { event } = this.props;
    const { session } = this.state.attendance;
    const setAttendanceUrl = routes.IN_PERSON_TRAINING.EVENT_SET_ATTENDANCE(
      event.id,
      session.id,
    );
    const fromSharedLink =
      this.props.attendance.created_from === 'created_from_shareable_link';
    this.setState({
      statusLoading: attendanceStatus,
      rsvpStatus: attendanceStatus,
    });
    request
      .post(setAttendanceUrl, {
        status: attendanceStatus,
        shared: fromSharedLink,
      })
      .then(json => {
        this.setState({
          attendance: json.attendance,
          changingAttendance: false,
          statusLoading: null,
        });
      })
      .fail(() => {
        this.setState({
          changingAttendance: false,
          statusLoading: null,
        });
      });
  };

  // Preforms the same function as `setUserAttendance`, but uses the new
  // /training/ namespace API. Once the feature flag is deprecated, we can
  // remove the old setUserAttendance and rename this.
  setUserAttendanceNewAPI = attendanceStatus => {
    const { event, meta, pastEvent } = this.props;
    const { session } = this.state.attendance;
    const setAttendanceUrl =
      pastEvent || event.rsvp_type === 'single'
        ? routes.TRAINING.SESSION_SET_ATTENDANCE(event.id, session.id)
        : routes.TRAINING.EVENT_SET_RSVP(event.id);
    const fromSharedLink =
      this.props.attendance.created_from === 'created_from_shareable_link';

    this.setState({
      statusLoading: attendanceStatus,
      rsvpStatus: attendanceStatus,
    });

    const status = pastEvent
      ? { attendanceStatus: attendanceStatus ? 'did_attend' : 'did_not_attend' }
      : { rsvpStatus: attendanceStatus ? 'will_attend' : 'will_not_attend' };

    request
      .putJSON(setAttendanceUrl, {
        ...status,
        userId: meta.currentUser.id,
        createdFrom: fromSharedLink ? 'created_from_shareable_link' : null,
      })
      .then(json => {
        const sessionResponse = json.data.sessions.find(
          sessionInfo => sessionInfo.id === session.id,
        );

        this.setState(state => ({
          attendance: {
            ...state.attendance,
            rsvp_status: sessionResponse.rsvpStatus,
            attendance_status: sessionResponse.attendanceStatus,
            did_attend: sessionResponse.didAttend,
          },
          changingAttendance: false,
          statusLoading: null,
        }));
      })
      .fail(() => {
        this.setState({
          changingAttendance: false,
          statusLoading: null,
        });
      });
  };

  changeAttendance = () => {
    this.setState({
      changingAttendance: true,
    });
  };

  displayRsvpConfirmation = () => {
    const { rsvpStatus } = this.state;

    return (
      (rsvpStatus === true && !this.state.changingAttendance) ||
      (getQueryParam(window.location.href, 'response') === 'will_attend' &&
        rsvpStatus !== false)
    );
  };

  displayRsvpRejection = () => {
    const { rsvpStatus } = this.state;

    return (
      (rsvpStatus === false && !this.state.changingAttendance) ||
      (getQueryParam(window.location.href, 'response') === 'will_not_attend' &&
        rsvpStatus !== true)
    );
  };

  render() {
    const {
      event,
      pastEvent,
      meta: { policies },
    } = this.props;
    const { changingAttendance, attendance, statusLoading } = this.state;
    const { session } = this.state.attendance;

    // User an RSVP status set
    const hasRsvpInfo = some([
      attendance.did_attend !== null,
      attendance.rsvp_status !== null,
    ]);

    // Session has a max attendance set and session is at capacity
    const sessionIsAtCapacity = every([
      session.max_attendees !== null,
      session.total_registered >= session.max_attendees,
    ]);

    // User has not registered and session is at capacity
    const showSessionAtCapacityNotice = every([
      !pastEvent || policies.canChangeAttendance,
      !(attendance.did_attend || attendance.rsvp_status === 'will_attend'),
      sessionIsAtCapacity,
    ]);

    // User has attendance status
    const hasAttendanceStatus = some([
      hasRsvpInfo && !changingAttendance,
      pastEvent && !policies.canChangeAttendance,
    ]);

    const showAttendanceStatus =
      hasAttendanceStatus || showSessionAtCapacityNotice;

    const previousAttendanceForm = (
      <AttendanceForm
        changeAttendance={this.changeAttendance}
        changingAttendance={changingAttendance}
        event={event}
        session={session}
        pastEvent={pastEvent}
        attendance={attendance}
        setUserAttendance={this.setUserAttendance}
        statusLoading={statusLoading}
        canChangeAttendance={policies.canChangeAttendance}
      />
    );

    return (
      <div className="EventRegistration">
        <Header event={event} session={session} />
        <Card>
          <Card.Content padding="large">
            <div className="d-f fd-c">
              <Text variant={TEXT_VARIANTS.SUBHEADER_BOLD}>
                {t('in_person_training.event_details')}
              </Text>
            </div>
            {this.displayRsvpConfirmation() && (
              <Text className="mt-16 d-b" variant={TEXT_VARIANTS.BODY_LIGHT}>
                {t('in_person_training.rsvp_status.confirmation_message')}
              </Text>
            )}
            <div className="mt-16">
              <SessionDetails
                session={session}
                showRegistrationCap
                eventId={event.id}
              />
            </div>
            {showAttendanceStatus && (
              <div className="mt-16">
                <AttendanceStatus
                  attendance={attendance}
                  pastEvent={pastEvent}
                  showSessionAtCapacityNotice={showSessionAtCapacityNotice}
                />
              </div>
            )}
            {this.displayRsvpRejection() && (
              <Text className="fs-i mt-16 d-b" variant={TEXT_VARIANTS.BODY}>
                {t('in_person_training.rsvp_status.will_not_attend_reminder')}
              </Text>
            )}
          </Card.Content>
          <Card.Content className="bg-gray-lightest bt-gray-lighter px-24 py-16">
            <FeatureFlag
              features={['events_in_development']}
              fallback={previousAttendanceForm}
            >
              <AttendanceForm
                changeAttendance={this.changeAttendance}
                changingAttendance={changingAttendance}
                event={event}
                session={session}
                pastEvent={pastEvent}
                attendance={attendance}
                setUserAttendance={this.setUserAttendanceNewAPI}
                statusLoading={statusLoading}
                canChangeAttendance={policies.canChangeAttendance}
              />
            </FeatureFlag>
          </Card.Content>
        </Card>
      </div>
    );
  }
}

export default EventRegistrationOverview;
